import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Eye, Download, Loader2, Users, Package, Layers } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { DateField } from "@/components/DateField";
import api, { openInNewTab, fmtDate } from "@/lib/api";

const STATUSES = ["New", "With Karigar", "Ready", "In Shop", "Delivered", "Cancelled"];
const PRESETS = [["all", "All Time"], ["today", "Today"], ["yesterday", "Yesterday"], ["week", "This Week"], ["month", "This Month"], ["custom", "Custom"]];
const isoToday = () => new Date().toLocaleDateString("en-CA");

export const KarigarReportDialog = ({ open, onOpenChange }) => {
  const [karigar, setKarigar] = useState("all");
  const [status, setStatus] = useState("All");
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState({ from: isoToday(), to: isoToday() });
  const [busy, setBusy] = useState("");

  const dates = useMemo(() => {
    const now = new Date();
    const iso = (d) => d.toLocaleDateString("en-CA");
    const t = iso(now);
    const back = (n) => iso(new Date(now.getFullYear(), now.getMonth(), now.getDate() - n));
    switch (preset) {
      case "all": return { from: "", to: "" };
      case "today": return { from: t, to: t };
      case "yesterday": return { from: back(1), to: back(1) };
      case "week": return { from: back((now.getDay() + 6) % 7), to: t };
      case "month": return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: t };
      default: return range;
    }
  }, [preset, range]);

  const params = { karigar, status, from: dates.from, to: dates.to };

  const { data: karigars } = useQuery({ queryKey: ["karigars"], queryFn: async () => (await api.get("/karigars")).data, enabled: open });
  const { data, isFetching } = useQuery({
    queryKey: ["karigar-report", karigar, status, dates.from, dates.to],
    queryFn: async () => (await api.get("/products/report/data", { params })).data,
    enabled: open,
  });
  const s = data?.summary;

  const previewPdf = () => {
    setBusy("preview");
    openInNewTab(async () => {
      const res = await api.get("/products/report/pdf", { params, responseType: "blob" });
      return URL.createObjectURL(res.data);
    }).catch(() => toast.error("Report generate nahi hui")).finally(() => setBusy(""));
  };

  const downloadPdf = async () => {
    setBusy("download");
    try {
      const res = await api.get("/products/report/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = preset === "all" ? "Karigar-Product-Report-All-Time.pdf" : `Karigar-Product-Report-${dates.from}-to-${dates.to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Report PDF downloaded");
    } catch {
      toast.error("Report generate nahi hui");
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="karigar-report-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Karigar Product Report</DialogTitle>
          <DialogDescription>Filter products by karigar, status and date — then generate a PDF report.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Karigar</p>
              <Select value={karigar} onValueChange={setKarigar}>
                <SelectTrigger data-testid="report-filter-karigar" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Karigars</SelectItem>
                  {(karigars || []).map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Product Status</p>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="report-filter-status" className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>{["All", ...STATUSES].map((st) => <SelectItem key={st} value={st}>{st === "All" ? "All Status" : st}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Date Range</p>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map(([v, l]) => (
                <button key={v} data-testid={`report-preset-${v}`} onClick={() => setPreset(v)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium border transition-colors ${preset === v ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground hover:text-foreground"}`}>
                  {l}
                </button>
              ))}
            </div>
            {preset === "custom" && (
              <div className="flex items-center gap-2 mt-2">
                <div className="w-[160px]"><DateField testId="report-from-date" value={range.from} onChange={(v) => setRange({ ...range, from: v })} /></div>
                <span className="text-muted-foreground text-sm">to</span>
                <div className="w-[160px]"><DateField testId="report-to-date" value={range.to} onChange={(v) => setRange({ ...range, to: v })} /></div>
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-2">
              {preset === "all" ? "All Time — every product included" : `${fmtDate(dates.from)} — ${fmtDate(dates.to)}`} · Date applies to Sent-to-Karigar date (order date if not sent yet)
            </p>
          </div>

          <div className="rounded-xl border bg-muted/30 p-4" data-testid="report-summary">
            {isFetching ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading matching products...</div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  {[[Package, "Products", s?.total_products ?? 0, "report-total-products"],
                    [Layers, "Pieces", s?.total_pieces ?? 0, "report-total-pieces"],
                    [Users, "Customers", s?.customers ?? 0, "report-total-customers"]].map(([Icon, l, v, tid]) => (
                    <div key={l} className="text-center">
                      <Icon className="h-4 w-4 mx-auto text-amber-500" />
                      <p className="text-lg font-semibold mt-1" data-testid={tid}>{v}</p>
                      <p className="text-[11px] text-muted-foreground">{l}</p>
                    </div>
                  ))}
                </div>
                {s && Object.keys(s.by_status || {}).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t justify-center">
                    {Object.entries(s.by_status).map(([st, c]) => (
                      <span key={st} className="px-2 py-0.5 rounded-full bg-card border text-[11px] text-muted-foreground">{st}: <span className="font-medium text-foreground">{c}</span></span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" data-testid="report-preview-btn" disabled={!!busy || isFetching} onClick={previewPdf}>
              {busy === "preview" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Eye className="h-4 w-4 mr-1.5" />} Preview PDF
            </Button>
            <Button data-testid="report-download-btn" disabled={!!busy || isFetching} onClick={downloadPdf}>
              {busy === "download" ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />} Generate & Download PDF
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
